import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';

export interface DonutChartData {
  name: string;
  value: number;
  color: string;
}

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [CommonModule],
  template: '<div #chart class="donut-chart-container"></div>',
  styles: [`
    .donut-chart-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `]
})
export class DonutChartComponent implements AfterViewInit, OnChanges {
  @ViewChild('chart', { static: false }) private chartContainer!: ElementRef;

  @Input() data: DonutChartData[] = [];
  @Input() colors?: string[];
  @Input() innerRadiusRatio: number = 0.5;
  @Input() variableArcWidths: boolean = false;
  @Input() arcReductionPercent: number = 0.2;
  @Input() width: number = 175;
  @Input() height: number = 175;
  @Input() showPercentageOnHover: boolean = false;
  @Input() showPercentageAlways: boolean = false;
  @Input() percentageFontSize: number = 13; // Configurable font size for percentage labels

  @Output() segmentHover = new EventEmitter<{ percentage: number; name: string } | null>();

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chartContainer && !changes['data']?.firstChange) {
      this.createChart();
    }
  }

  private createChart(): void {
    const element = this.chartContainer.nativeElement;
    const radius = Math.min(this.width, this.height) / 2;

    // Clear existing chart
    d3.select(element).select('svg').remove();

    // Add padding for pop-out effect
    const padding = (this.showPercentageOnHover || this.showPercentageAlways) ? 15 : 0;

    const svg = d3.select(element)
      .append('svg')
      .attr('width', this.width + padding * 2)
      .attr('height', this.height + padding * 2)
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', `translate(${(this.width + padding * 2) / 2},${(this.height + padding * 2) / 2})`);

    const pie = d3.pie<DonutChartData>()
      .value(d => d.value)
      .sort(null);

    const pieData = pie(this.data);

    // Calculate arc radii
    const baseInnerRadius = radius * this.innerRadiusRatio;
    const baseThickness = 30;

    // Sort data by value descending for variable arc widths
    const sortedData = [...this.data].sort((a, b) => b.value - a.value);

    const arcs = svg.selectAll('.arc')
      .data(pieData)
      .enter()
      .append('g')
      .attr('class', 'arc');

    // Helper function to create arc generator
    const createArc = (d: any, hoverOffset: number = 0) => {
      let innerRadius = baseInnerRadius;
      let outerRadius: number;

      if (this.variableArcWidths) {
        const sortedIndex = sortedData.findIndex(item => item.name === d.data.name);
        const thickness = baseThickness * Math.pow(1 - this.arcReductionPercent, sortedIndex);
        outerRadius = innerRadius + thickness;
      } else {
        outerRadius = radius;
      }

      return d3.arc<d3.PieArcDatum<DonutChartData>>()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius + hoverOffset);
    };

    const paths = arcs.append('path')
      .attr('d', d => {
        const arc = createArc(d, 0);
        return arc(d) || '';
      })
      .attr('fill', d => {
        if (this.colors && this.colors.length > 0) {
          const index = this.data.findIndex(item => item.name === d.data.name);
          return this.colors[index % this.colors.length];
        }
        return d.data.color;
      })
      .attr('stroke', 'none')
      .style('cursor', (this.showPercentageOnHover || this.showPercentageAlways) ? 'pointer' : 'default');

    // Add percentage labels on hover if enabled or always visible
    if (this.showPercentageOnHover || this.showPercentageAlways) {
      const totalValue = this.data.reduce((sum, d) => sum + d.value, 0);

      // Add text elements for percentages
      const texts = arcs.append('text')
        .attr('transform', d => {
          let innerRadius = baseInnerRadius;
          let outerRadius: number;

          if (this.variableArcWidths) {
            const sortedIndex = sortedData.findIndex(item => item.name === d.data.name);
            const thickness = baseThickness * Math.pow(1 - this.arcReductionPercent, sortedIndex);
            outerRadius = innerRadius + thickness;
          } else {
            outerRadius = radius;
          }

          const arc = d3.arc<d3.PieArcDatum<DonutChartData>>()
            .innerRadius(innerRadius)
            .outerRadius(outerRadius);

          const centroid = arc.centroid(d);
          return `translate(${centroid[0]}, ${centroid[1]})`;
        })
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('font-size', `${this.percentageFontSize}px`)
        .style('font-weight', '700')
        .style('fill', '#fff')
        .style('pointer-events', 'none')
        .style('opacity', this.showPercentageAlways ? '1' : '0')
        .text(d => `${Math.round((d.data.value / totalValue) * 100)}%`);

      // Hover effects with pop-out animation
      const self = this;
      paths
        .on('mouseenter', function(event: any, d: any) {
          // Pop out effect - increase outer radius by 10 pixels
          const arc = createArc(d, 10);
          d3.select(this)
            .transition()
            .duration(200)
            .attr('d', arc(d) || '');

          // Hide all labels first
          texts
            .transition()
            .duration(200)
            .style('opacity', '0');

          // Show percentage for hovered segment only
          texts.filter((td: any) => td.data.name === d.data.name)
            .transition()
            .duration(200)
            .style('opacity', '1');

          // Emit hover event with percentage
          const percentage = Math.round((d.data.value / totalValue) * 100);
          self.segmentHover.emit({ percentage, name: d.data.name });
        })
        .on('mouseleave', function(event: any, d: any) {
          // Return to normal size
          const arc = createArc(d, 0);
          d3.select(this)
            .transition()
            .duration(200)
            .attr('d', arc(d) || '');

          // Restore percentage visibility based on mode
          if (self.showPercentageAlways) {
            // Show all percentages again
            texts
              .transition()
              .duration(200)
              .style('opacity', '1');
          } else {
            // Hide percentage
            texts
              .transition()
              .duration(200)
              .style('opacity', '0');
          }

          // Emit null to reset
          self.segmentHover.emit(null);
        });
    }
  }
}